import Seo from "@/components/Seo";
import { useState } from "react";
import axios from "axios";
import { CUSTOM_SERVICES, IMG, BRAND } from "@/data/mockData";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomOrders() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: "custom", subject: "Custom order enquiry" });
      toast("Enquiry sent", { description: "We'll write back within 24 hours." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast("Couldn't send", { description: "Please try again shortly." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-custom-orders" className="pt-32 pb-24">
      <Seo path="/custom-orders" title="Custom Silver Jewellery" description="Custom silver jewellery, engraved coins, wedding return gifts and religious articles — hand-crafted by SIRIJA, Pune." />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <div className="overline">Custom orders</div>
            <h1 className="mt-6 font-serif font-light text-5xl lg:text-6xl leading-[1] tracking-tight">
              Bring us a sketch. <span className="italic silver-shimmer">We'll bring it to life.</span>
            </h1>
            <p className="mt-8 text-[15px] leading-relaxed text-[#2E2E2E]/75 max-w-lg font-light">
              From a name-etched pendant to a full bridal ensemble, our karigars can craft nearly anything in silver. Share a reference or a memory — we'll take it from there.
            </p>
            <div className="mt-12 space-y-4">
              {CUSTOM_SERVICES.map((s, i) => (
                <div key={s.title} data-testid={`custom-svc-${i}`} className="flex items-start gap-5 p-5 rounded-2xl border border-[#EDEDED] hover:bg-[#F8F9FA] transition-colors">
                  <div className="h-9 w-9 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-serif text-xl tracking-tight">{s.title}</div>
                    <p className="mt-1 text-sm text-[#2E2E2E]/70">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <div className="rounded-2xl overflow-hidden mb-6 aspect-[4/3]">
              <img src={IMG.ring1} alt="Custom silver ring" className="w-full h-full object-cover" />
            </div>
            <form onSubmit={submit} data-testid="custom-form" className="p-8 rounded-2xl border border-[#EDEDED] bg-white">
              <h3 className="font-serif text-2xl tracking-tight">Tell us what you'd like</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline">Your name</label>
                  <Input required data-testid="custom-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
                <div>
                  <label className="overline">Phone</label>
                  <Input data-testid="custom-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
              </div>
              <div className="mt-4">
                <label className="overline">Email</label>
                <Input required type="email" data-testid="custom-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
              </div>
              <div className="mt-4">
                <label className="overline">The piece you have in mind</label>
                <Textarea required rows={4} data-testid="custom-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-lg border-[#E5E5E5]" placeholder="Occasion, budget range, any reference images or names to engrave…" />
              </div>
              <button type="submit" disabled={busy} data-testid="custom-submit" className="btn-fill mt-6 h-12 px-7 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] hover:text-white disabled:opacity-60">
                {busy ? "Sending…" : "Send enquiry"}
              </button>
              <p className="mt-4 text-xs text-[#A0A0A0]">Prefer WhatsApp? Message us at {BRAND.phone}.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
